import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useDefaults } from '@/store/defaults';

/**
 * Read-only показ «який рахунок СПРАЦЮЄ у проводці» для номенклатури.
 *
 * Дані — з матриці account_rules через прод-резолвер (GET /refs/account-rules/preview),
 * НIЧОГО не пише. 1С-міграція кладе рахунки у матрицю, а не в плоске поле картки —
 * цей блок показує бухгалтеру його реальний рахунок (напр. 25), а не пусто/281.
 *
 * Правила — per-юрособа, тому є селектор юрособи (default — перша accessible) +
 * опц. селектор складу. Якщо рахунок РЕАЛЬНО залежить від СКЛАДУ (різні склади →
 * різний рахунок, перевірено симуляцією резолвера на беку) — показуємо БАНЕР
 * «залежить від складу» (бо проводка передасть doc.warehouse_id і візьме інший
 * рахунок), а не єдиний рахунок — інакше UI збрехав би. Вид (kind) фіксований
 * per-номенклатура → не створює залежності, рахунок показується напряму.
 */
export function NomenclatureAccountPreview({ nomenclatureId, onResolved }: {
  nomenclatureId: string;
  /** 2026-08-12: віддає резолвнутий код нагору (живий placeholder полів картки). */
  onResolved?: (code: string | null, sourceLabel: string) => void;
}) {
  const orgsQ = useQuery({ queryKey: ['orgs-preview'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgList = orgsQ.data?.items ?? [];
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [warehouseId, setWarehouseId] = useState('');

  // default org — перша accessible (патерн RefsPage).
  const effectiveOrg = orgId || orgList[0]?.id || '';

  const warehousesQ = useQuery({
    queryKey: ['warehouses-preview', effectiveOrg],
    queryFn: () => refs.warehouses(effectiveOrg),
    enabled: !!effectiveOrg,
  });

  const previewQ = useQuery({
    queryKey: ['account-rule-preview', effectiveOrg, nomenclatureId, warehouseId],
    queryFn: () => refs.accountRules.preview({
      organization_id: effectiveOrg,
      nomenclature_id: nomenclatureId,
      ...(warehouseId ? { warehouse_id: warehouseId } : {}),
    }),
    enabled: !!effectiveOrg && !!nomenclatureId,
  });

  const p = previewQ.data;
  const showsBanner = useMemo(
    () => !!p && p.warehouse_dependent && !warehouseId,
    [p, warehouseId],
  );
  useEffect(() => {
    if (!onResolved) return;
    if (p && !showsBanner && p.source !== 'not-used-in-postings') {
      onResolved(p.account_code ?? null, sourceLabel(p.source));
    } else {
      onResolved(null, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p, showsBanner]);

  // 2026-08-12: детальний бейдж рівня правила («логіка як у 1С»).
  const sourceLabel = (src: string | undefined): string => {
    switch (src) {
      case 'rule':
        switch (p?.rule_level) {
          case 'kind':
            return p?.rule_kind_name
              ? `за правилом виду «${p.rule_kind_name}»`
              : 'за правилом виду';
          case 'nomenclature': return 'точкове правило цієї позиції';
          case 'warehouse': return 'правило складу';
          case 'org': return 'правило організації';
          default: return 'правило обліку';
        }
      case 'card': return 'поле картки (override)';
      case 'hardcode-fallback': return 'стандартний (281)';
      case 'not-used-in-postings': return 'не застосовується у проводках';
      default: return '';
    }
  };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 6, padding: '0.75rem',
      background: 'var(--bg-soft-2)', display: 'flex',
      flexDirection: 'column', gap: '0.5rem',
    }}>
      <strong style={{ fontSize: '0.9em' }}>Який рахунок застосується у проводках</strong>
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: '0.8em' }}>
        Рахунок береться з правил обліку. Поля нижче — лише override
        картки для простих випадків.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.85em' }}>Юрособа
          <select value={effectiveOrg} onChange={(e) => { setOrgId(e.target.value); setWarehouseId(''); }}>
            {orgList.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: '0.85em' }}>Склад (опц.)
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">— будь-який —</option>
            {(warehousesQ.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
      </div>

      {previewQ.isLoading && <span style={{ fontSize: '0.85em', color: 'var(--muted)' }}>…</span>}

      {p && showsBanner && (
        <div style={{
          padding: '0.5rem', borderRadius: 4, background: '#fff6e0',
          border: '1px solid #e0c060', fontSize: '0.85em',
        }}>
          Рахунок <b>залежить від складу</b> — для різних складів він різний.
          Оберіть склад вище, або дивіться Довідники → Рахунки обліку (правила).
        </div>
      )}

      {p && !showsBanner && (
        <div style={{ fontSize: '0.95em' }}>
          {p.source === 'not-used-in-postings' ? (
            <span style={{ color: 'var(--muted)' }}>Не застосовується у проводках</span>
          ) : (
            <>
              <b>{p.account_code ?? '—'}</b>
              {p.account_name ? ` — ${p.account_name}` : ''}
              <span style={{
                marginLeft: '0.5rem', fontSize: '0.8em', color: 'var(--muted)',
                padding: '1px 6px', border: '1px solid var(--border)', borderRadius: 10,
              }}>
                {sourceLabel(p.source)}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
